<template>
  <div id="app">
    <component :is="layout">
      <router-view />
    </component>
    <alert-msg />
  </div>
</template>
<script>
import { initializeNotifications } from "@/api/pushNotificationApi";
import { mapGetters } from "vuex";
import AlertMsg from "@/components/widgets/alertMsg";

export default {
  name: "App",
  components: {
    AlertMsg,
    DefaultLayout: () => import(/* webpackPrefetch: true */ "@/layouts/default"),
    EmptyLayout: () => import(/* webpackPrefetch: true */ "@/layouts/empty"),
    OnlyHeaderLayout: () => import(/* webpackPrefetch: true */ "@/layouts/onlyHeader"),
    PageFullscreenLayout: () => import(/* webpackPrefetch: true */ "@/layouts/pageFullscreen")
  },
  data() {
    return {
      queueUpdateType: [],
      dequeueInProgress: undefined
    };
  },
  computed: {
    ...mapGetters({
      authenticated: "User/isAuthenticated",
      user: "User/getUser",
      selectedGroup: "User/getSelectedGroup",
      isMachineLoading: "Machine/isLoading",
      selectedMachine: "Machine/getSelected",
      machinesRaw: "Machine/getMachinesRaw"
    }),
    isLoading() {
      return this.isMachineLoading && this.dequeueInProgress;
    },
    layout() {
      return this.$route.meta.layout || "default-layout";
    },
    userId() {
      return this.user && this.user.Id;
    },
    selectedGroupId() {
      return this.selectedGroup && this.selectedGroup.Id;
    },
    selectedMachineId() {
      return this.selectedMachine && this.selectedMachine.Id;
    },
    machinesIsEmpty() {
      return Object.keys(this.machinesRaw).length === 0;
    },
    routeMachineId() {
      return this.$route.params.id || this.$route.params.machineId;
    }
  },
  watch: {
    user() {
      initializeNotifications(this.userId);
    },
    $route: {
      immediate: true,
      handler(newValue, oldValue) {
        this.onRouteChange(newValue, oldValue);
      }
    },
    selectedGroupId: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.addUpdateTypeToQueue(["machines"]);
        }
      }
    },
    selectedMachineId: {
      immediate: true,
      handler(newValue) {
        if (this.routeMachineId && (!newValue || newValue !== this.routeMachineId)) {
          this.addUpdateTypeToQueue(["machine"]);
        }
      }
    }
  },
  async updated() {
    try {
      await this.checkStoreAuthentication();
    } catch (e) {
      console.error("Check store authentication error: ", e && e.message);
    }
  },
  created() {
    this.loginMe();
  },
  methods: {
    loginMe() {
      const isGuest = this.$route.meta && this.$route.meta.guest;

      if (this.$route.name !== "login" && !isGuest) {
        this.$store.dispatch("User/loginMe");
      }
    },
    async checkStoreAuthentication() {
      if (!this.authenticated) {
        // Go Login
        if (this.$route.name !== "login" && this.$route.name !== "external_login_callback") {
          if (localStorage.length > 0) {
            await this.$store.dispatch("User/logout");
          }
          await this.$router.push({ name: "login" }).catch(() => {});
        }
      }
    },
    async onRouteChange(newValue, oldValue) {
      // **************************************
      // * INIT MACHINE STORE ON ROUTE CHANGE *
      // **************************************
      const newId = newValue.params.id || newValue.params.machineId;
      const oldId = oldValue && (oldValue.params.id || oldValue.params.machineId);
      const updateMachines = oldValue && oldValue.name !== "login";
      const updateMachine = newId && (!this.selectedMachineId || (oldValue && oldId !== newId));
      const updateTypes = [];

      //
      // Check roles page
      //
      if (this.userId) {
        await this.$store.dispatch("User/checkRolesAllowedOnPage");
      }

      if (updateMachines) {
        updateTypes.push("machines");
      }

      if (updateMachine) {
        updateTypes.push("machine");
      }

      await this.addUpdateTypeToQueue(updateTypes);
    },
    async addUpdateTypeToQueue(types) {
      if (types) {
        const newType = (types || []).reduce((acc, el) => {
          const lastEl = acc.length > 0 && acc[acc.length - 1];

          if (
            lastEl !== el &&
            ((lastEl !== "machines" && el !== this.dequeueInProgress) || lastEl === "machines")
          ) {
            acc.push(el);
          }

          return acc;
        }, []);

        this.queueUpdateType = newType;
      }

      await this.dequeueUpdateType();
    },
    async dequeueUpdateType() {
      if (!this.isLoading && this.queueUpdateType.length > 0) {
        this.dequeueInProgress = this.queueUpdateType.shift();

        if (this.dequeueInProgress === "machine") {
          const newId = this.routeMachineId;
          await this.updateMachine(newId);
        } else if (this.dequeueInProgress === "machines") {
          await this.refreshMachines();
        }

        this.dequeueInProgress = undefined;

        this.dequeueUpdateType();
      }
    },
    async updateMachine(newId) {
      try {
        await this.$store.dispatch("Machine/clearSelected");

        if (newId) {
          await this.$store.dispatch("Machine/selectMachine", {
            machineId: newId,
            self: this
          });
        }
      } catch (e) {
        console.error("[Error] updateMachine failed:", e);
      }
    },
    async refreshMachines() {
      const groupId = this.selectedGroupId;
      const path = this.$route.path;
      const updateMap = {
        "/analytics": false,
        "/machine": true,
        "/machine/timelines": true,
        "/login": true,
        "/external-login-callback": true
      };

      if (groupId && path !== "/") {
        try {
          const updateStatus = !!updateMap[path];

          if (updateStatus) {
            await this.$store.dispatch("Machine/fetchMachines", {
              groupId,
              updateStatus
            });
          } else {
            await this.$store.dispatch("Machine/refreshMachines", { groupId });
          }
        } catch (e) {
          console.error("[Error] refreshMachines failed:", e);
        }
      }
    }
  }
};
</script>
